import React, { useEffect, useState, useCallback } from "react";
import { FormControl, CircularProgress, TextField, styled, Link, useTheme, Box } from "@mui/material";
import { Roles, CustomerDTO, SiteDTO } from "@beable/roster-backend-client-v1";
import authService from "../../services/AuthService";
import provisionService from "../../services/ProvisionService";
import {
  CustomerActionType,
  useCustomerDispatch,
  useCustomerState,
} from "../../context/CustomerContext";
import { Autocomplete } from '@mui/material';
import FindSiteDialog from "./Dialogs/FindSiteDialog";
import { getCustomerSelectorStyles } from './styles'

interface CustomerSelectorProps {
  customerId?: string | undefined;
  locationCode?: string;
  onSelect: any;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  variant?: "filled" | "outlined";
  hideSearch?: boolean;
  ignoreState?: boolean;
  noAutoSelect?: boolean
  inPanel?: boolean;
}

export default function CustomerSelector({
  customerId,
  locationCode,
  onSelect,
  className,
  disabled,
  required,
  variant,
  hideSearch,
  ignoreState,
  noAutoSelect,
  inPanel,
}: CustomerSelectorProps): JSX.Element {
  const user = authService.getUser();
  const customerDispatch = useCustomerDispatch();
  let customerState = useCustomerState();

  if (ignoreState) {
    customerState = {
      customer: undefined,
      site: undefined
    };
  }

  const [selectedCustomer, setSelectedCustomer] = useState<
    CustomerDTO | undefined
  >(undefined);
  const [customers, setCustomers] = useState<CustomerDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [searchBySite, setSearchBySite] = useState(false);

  const theme = useTheme();
  const classes = getCustomerSelectorStyles(theme);
  const memoOnSelect = useCallback(onSelect, []);

  useEffect(() => {
    const fetchCustomers = async () => {
      let customers = await provisionService.getCustomers();
      if (customers) {
        if (locationCode) {
          customers = customers.filter(
            (customer) => customer.locationCode === locationCode
          );
        }
        setCustomers(customers);

        let customer;
        if (customerId) {
          customer = customers.find((c) => c.id === customerId);
        }

        if (customers.length === 1 && !noAutoSelect) {
          customer = customers[0];
        }

        setSelectedCustomer(customer);
        memoOnSelect(customer);
        setInitialLoad(false);
      } else {
        window.alert("No customer(s) found...");
      }
      setIsLoading(false);
    };

    setIsLoading(true);
    fetchCustomers();
  }, [memoOnSelect, customerId, locationCode]);

  useEffect(() => {
    if (
      !initialLoad &&
      !selectedCustomer &&
      customers.length &&
      customerState.customer
    ) {
      const customer = customers.find(
        (c) => c.id === customerState.customer?.id
      );
      if (customer) {
        setSelectedCustomer(customer);
        memoOnSelect(customer);
      }
    }
  }, [memoOnSelect, customers, customerState, selectedCustomer, initialLoad]);

  const handleCustomerChange = (_event: any, customer: CustomerDTO | null) => {
    const selected: CustomerDTO | undefined = customers.find(
      (cust: CustomerDTO) => cust.id === customer?.id
    );
    setSelectedCustomer(selected);
    onSelect(selected);
    customerDispatch({
      type: CustomerActionType.CustomerChanged,
      customer: selected,
    });
  };

  const setCustomerBySite = (site: SiteDTO) => {
    const selItem = customers.find(
      (cust: CustomerDTO) => cust.id === site.customerId
    );

    if (selItem && selItem.id !== selectedCustomer?.id) {
      handleCustomerChange(null, selItem);
      customerDispatch({
        type: CustomerActionType.SiteChanged,
        site: site,
      });
    }
  };

  return (
    <>
      {isLoading && (
        <Box sx={classes.loaderContainer}>
          <CircularProgress disableShrink />
        </Box>
      )}
      {user && user.role === Roles.beableadmin && !isLoading && (
        <>
          <FormControl
            className={className}
            sx={inPanel ? classes.selectListPanel : (className ? undefined : classes.selectList)}
            variant={ variant || "standard" }
          >
            <Autocomplete
              disabled={disabled}
              options={customers}
              value={selectedCustomer?.id ? selectedCustomer : null}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search for a customer"
                  size="medium"
                  label={`Customer${required ? "*" : ""}`}
                  variant={ variant || "standard" }
                />
              )}
              onChange={handleCustomerChange}
              size="medium"
              id="select-customer"
              data-cy="customer-select"
              renderOption={(props, option: CustomerDTO) => (
                <li {...props} key={option.id}>
                  <div >{option.name}</div>
                </li>
              )}
            />
          </FormControl>
          {!hideSearch && (
            <>
              <Box sx={{ cursor: disabled ? "default" : "pointer", textAlign: "right", width: "100%", pr: 1.25 }}>
                <Link
                  variant="subtitle2"
                  align="right"
                  onClick={() => {
                    if (!disabled) {
                      setSearchBySite(true);
                    }
                  }}
                  underline={disabled ? 'none' : 'hover'}
                >
                  Search for site
                </Link>
              </Box>
              <FindSiteDialog
                open={searchBySite}
                onClose={() => setSearchBySite(false)}
                handleRowClick={setCustomerBySite}
                customers={customers}
            />
          </>
        )}
      </>
    )}
  </>);
}

export const ModalCustomerSelector = styled(CustomerSelector)(({ theme }) => getCustomerSelectorStyles(theme).modalSelector);